import axios from 'axios';
import * as qs from 'qs';
import { payloadLoadCreateReport } from 'services/leads';
import { CreateConditionReportDto } from '../app/pages/FileDetail/components/ModalReport';
import { StorageServices } from 'services/storage';
import { TFile } from 'types/user';

export const getUpdatesCommunication = async (params?: {
  tab?: string;
  startDate?: string | null;
  endDate?: string | null;
  searchText?: string;
  page: number;
  size: number;
}) => {
  const query = qs.stringify({
    ...params
  });
  const { data: res } = await axios.get(`/communication/updates?${query}`);
  return res;
};
const storage = new StorageServices();

const MAX_RECONNECT_ATTEMPTS = 5;
const INITIAL_RECONNECT_DELAY_MS = 1000;

export const subscribeLastReadStatus = (
  onMessage: (data: { isRead: boolean }) => void,
  /* eslint-disable @typescript-eslint/no-explicit-any */
  onError?: (err: any) => void,
  options?: { maxReconnectAttempts?: number; reconnectDelayMs?: number }
) => {
  const maxAttempts = options?.maxReconnectAttempts ?? MAX_RECONNECT_ATTEMPTS;
  const baseDelay = options?.reconnectDelayMs ?? INITIAL_RECONNECT_DELAY_MS;
  let reconnectAttempts = 0;
  let retryTimeoutId: ReturnType<typeof setTimeout> | null = null;
  let currentEs: EventSource | null = null;
  let cancelled = false;

  const baseUrl = process.env.REACT_APP_API_URL;
  const token = storage.getAccessToken();
  if (!baseUrl || !token) {
    onError?.(new Error('Missing API URL or access token'));
    return () => {};
  }

  const connect = () => {
    if (cancelled) return;
    currentEs = new EventSource(`${baseUrl}/communication/inbox/last-read-status?token=${token}`);

    currentEs.addEventListener('lastReadStatus', (event) => {
      try {
        const data = JSON.parse((event as MessageEvent).data);
        onMessage(data);
        reconnectAttempts = 0;
      } catch (e) {
        console.error('Failed to parse SSE data', e);
      }
    });

    currentEs.onerror = (err) => {
      currentEs?.close();
      currentEs = null;
      onError?.(err);
      if (cancelled || reconnectAttempts >= maxAttempts) return;
      const delay = baseDelay * Math.pow(2, reconnectAttempts);
      reconnectAttempts += 1;
      retryTimeoutId = setTimeout(connect, delay);
    };
  };

  connect();

  return () => {
    cancelled = true;
    if (retryTimeoutId) {
      clearTimeout(retryTimeoutId);
      retryTimeoutId = null;
    }
    currentEs?.close();
    currentEs = null;
  };
};

export const subscribeInbox = (
  onMessage: (data: { files: TFile[]; total: number }) => void,
  /* eslint-disable @typescript-eslint/no-explicit-any */
  onError?: (err: any) => void
) => {
  if (!storage.getAccessToken()) return;
  const es = new EventSource(
    `${process.env.REACT_APP_API_URL}/files/inbox/sse?token=${storage.getAccessToken()}`
  );

  es.addEventListener('inbox', (event) => {
    try {
      const data = JSON.parse((event as MessageEvent).data);
      onMessage(data);
    } catch (e) {
      console.error('Failed to parse inbox SSE data', e);
    }
  });

  es.onerror = (err) => {
    onError?.(err);
    es.close();
  };

  return () => {
    es.close();
  };
};

export const markNotesAsRead = async (fileId: number) => {
  const { data: res } = await axios.put(`/communication/inbox/mark-read/${fileId}`);
  return res;
};

export const getSeizureReportCommunication = async (params?: {
  startDate?: string | null;
  endDate?: string | null;
  searchText?: string;
  page: number;
  size: number;
}) => {
  const query = qs.stringify({
    ...params
  });
  const { data: res } = await axios.get(`/communication/seizure-reports?${query}`);
  return res;
};

export const updateSeizureReport = async (
  id: number,
  payload: Partial<payloadLoadCreateReport>
) => {
  const { data: res } = await axios.put(`/communication/seizure-reports/${id}`, payload);
  return res;
};

export const createReport = async (payload: CreateConditionReportDto) => {
  const formData = new FormData();
  Object.entries(payload).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    if (Array.isArray(value)) {
      if (key === 'files') {
        (value as File[])?.forEach((v) => formData.append('files', v));
      } else {
        formData.append(key, JSON.stringify(value));
      }
    } else {
      formData.append(key, value as string);
    }
  });
  const { data } = await axios.post('/condition-reports', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return data;
};
